import { createContext, useContext, useEffect, useState } from "react";

const GoogleMapsContext = createContext(null);
const SCRIPT_ID = "google-maps-script";

export function GoogleMapsProvider({ children }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  // Temporarily hardcoded for GitHub Actions deployment
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "AIzaSyDoBL3eAOcA9IdH1Sf3c9C8uiBqzE9Pl2k";

  useEffect(() => {
    if (!apiKey) {
      console.error("[GoogleMapsProvider] Google Maps API key is not configured");
      setError("Google Maps API key is not configured");
      return;
    }

    // Check if script is already loaded
    if (window.google?.maps) {
      setIsLoaded(true);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.getElementById(SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener("load", () => setIsLoaded(true));
      existingScript.addEventListener("error", () => setError("Failed to load Google Maps"));
      return;
    }

    // Create new script if none exists
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.addEventListener("load", () => {
      setIsLoaded(true);
    });

    script.addEventListener("error", (e) => {
      console.error("[GoogleMapsProvider] Failed to load Google Maps script:", e);
      setError("Failed to load Google Maps");
    });

    document.head.appendChild(script);

    return () => {
      // Only remove the script if we added it and component is unmounting
      const scriptToRemove = document.getElementById(SCRIPT_ID);
      if (scriptToRemove && !isLoaded) {
        scriptToRemove.remove();
      }
    };
  }, [apiKey]);

  return <GoogleMapsContext.Provider value={{ isLoaded, error }}>{children}</GoogleMapsContext.Provider>;
}

export function useGoogleMaps() {
  const context = useContext(GoogleMapsContext);
  if (!context) {
    throw new Error("useGoogleMaps must be used within a GoogleMapsProvider");
  }
  return context;
}
